import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import Swal from "sweetalert2";

function OrderDetail() {
  const { id } = useParams(); // order_id
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getOrderById/${id}`);
      if (res.data.success && res.data.order) {
        setOrder(res.data.order);
      } else {
        Swal.fire("Error", "Order not found", "error");
      }
    } catch (err) {
      console.error("Fetch order error:", err);
      Swal.fire("Error", "Failed to fetch order", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  if (loading) return <div className="text-center py-5">Loading order...</div>;
  if (!order) return null;

  const { shipping_details, cart_items, totals, payments, order_status, created_at, updated_at } = order;

  // Order Status Timeline
  const statusSteps = ["Pending", "Confirmed", "Shipped", "Delivered", "Cancelled"];

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">
          <div className="d-flex flex-column flex-md-row gap-4">
            
            {/* Shipping Info */}
            <div className="card shadow-sm border-0 p-3 flex-shrink-0" style={{ width: 300 }}>
              <h5 className="mb-3">Shipping Details</h5>
              <p><strong>Name:</strong> {shipping_details.fullName}</p>
              <p><strong>Email:</strong> {shipping_details.email}</p>
              <p><strong>Phone:</strong> {shipping_details.phone}</p>
              <p><strong>Address:</strong> {shipping_details.address}, {shipping_details.city}, {shipping_details.state}, {shipping_details.pincode}</p>
                {/* <div className="text-center mt-3">
                    <Link to="/all-orders" className="btn btn-sm btn-outline-primary">Back</Link>
                </div> */}
            </div>

            {/* Invoice/Timeline */}
            <div className="flex-grow-1">
              <h5 className="mb-3">Order Invoice</h5>

              {/* Order Info */}
              <div className="card mb-3 shadow-sm border-0 p-3">
                <p><strong>Order ID:</strong> {order.order_uuid}</p>
                <p><strong>Created At:</strong> {new Date(created_at).toLocaleDateString()}</p>
                <p><strong>Updated At:</strong> {new Date(updated_at).toLocaleDateString()}</p>
              </div>

              {/* Timeline */}
              <div className="card mb-3 shadow-sm border-0 p-3">
                <h6>Order Status</h6>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  {statusSteps.map((status, idx) => {
                    const active = status === order_status || (statusSteps.indexOf(order_status) > idx);
                    return (
                      <div key={idx} className="text-center" style={{ flex: 1, position: "relative" }}>
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            backgroundColor: active ? "#198754" : "#ccc",
                            margin: "0 auto",
                            zIndex: 1,
                          }}
                        ></div>
                        {idx < statusSteps.length - 1 && (
                          <div
                            style={{
                              position: "absolute",
                              top: 10,
                              left: "50%",
                              width: "100%",
                              height: 2,
                              backgroundColor: statusSteps.indexOf(order_status) > idx ? "#198754" : "#ccc",
                              zIndex: 0,
                            }}
                          ></div>
                        )}
                        <small style={{ display: "block", marginTop: 5 }}>{status}</small>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Items Table */}
              <div className="card mb-3 shadow-sm border-0 p-3">
                <h6>Order Items</h6>
                <table className="table table-sm table-bordered mt-2">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart_items.map((item) => (
                      <tr key={item.cart_item_id}>
                        <td>
                          <img
                            src={item.product_image ? `${imgurl}${item.product_image}` : "https://via.placeholder.com/30"}
                            alt={item.product_data?.name}
                            style={{ width: 30, height: 30, objectFit: "cover", marginRight: 6 }}
                          />
                          {item.product_data?.name}
                        </td>
                        <td>{item.quantity}</td>
                        <td>₹{item.product_data?.price || item.total_price}</td>
                        <td>₹{(item.product_data?.price || item.total_price) * item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}><strong>Total MRP</strong></td>
                      <td>₹{totals.total_mrp}</td>
                    </tr>
                    <tr>
                      <td colSpan={3}><strong>Total Discount</strong></td>
                      <td>₹{totals.total_discount}</td>
                    </tr>
                    <tr>
                      <td colSpan={3}><strong>Total Amount</strong></td>
                      <td>₹{totals.total_amount}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Payments */}
              <div className="card mb-3 shadow-sm border-0 p-3">
                <h6>Payments</h6>
                {payments.length === 0 ? (
                  <p>No payment info</p>
                ) : (
                  <table className="table table-sm table-bordered mt-2">
                    <thead>
                      <tr>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.payment_id}>
                          <td>₹{p.amount}</td>
                          <td>{p.payment_status}</td>
                          <td>{p.payment_method}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default OrderDetail;
